import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setSelectedMic, setSelectedSpeaker, setAudioPopoverOpen, setSelectedLocale } from '../features/audioSlice';
import { CALL_LANGUAGES } from '../constants/callLanguages';
import { toggleDevice, setTheme } from '../features/deviceSlice';
import { toggleFollowMode, toggleMobileStatusBar, toggleEmployeeEvents, toggleIPadDesktopLandscape } from '../features/channelSlice';
import { toggleToasts } from '../features/notificationSlice';
import { isIPadDevice } from '../utils/deviceUtils';


/**
 * AudioDevicePopover — consolidated device & quick settings selector.
 * Includes: Device Frame, Microphone, Speaker, Alerts, Follow.
 */
export default function AudioDevicePopover({ isOpen, onClose, anchor = 'left' }) {
  const dispatch = useDispatch();
  const selectedMicId = useSelector((state) => state.audio.selectedMicId);
  const selectedSpeakerId = useSelector((state) => state.audio.selectedSpeakerId);
  const selectedLocale = useSelector((state) => state.audio.selectedLocale);
  const isLightMode = useSelector((state) => state.device.theme) === 'light';
  const deviceType = useSelector((state) => state.device.type);
  const followMode = useSelector((state) => state.channel.followMode);
  const showMobileStatusBar = useSelector((state) => state.channel.showMobileStatusBar);
  const employeeEventsEnabled = useSelector((state) => state.channel.employeeEventsEnabled);
  const iPadDesktopLandscape = useSelector((state) => state.channel.iPadDesktopLandscape);
  const toastsEnabled = useSelector((state) => state.notifications.toastsEnabled);
  const isIPad = isIPadDevice();

  const [micDevices, setMicDevices] = useState([]);
  const [speakerDevices, setSpeakerDevices] = useState([]);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const ref = useRef(null);

  const sinkIdSupported =
    typeof HTMLMediaElement !== 'undefined' &&
    typeof HTMLMediaElement.prototype.setSinkId === 'function';

  // Mirror open-state into Redux so GuidedTour can gate nudges off while the
  // popover is showing. Reported from the component itself so every call site
  // (footer, actions, call view) is covered without per-parent plumbing.
  useEffect(() => {
    dispatch(setAudioPopoverOpen(isOpen));
    return () => { dispatch(setAudioPopoverOpen(false)); };
  }, [isOpen, dispatch]);

  // Enumerate devices
  useEffect(() => {
    if (!isOpen) return;

    const enumerate = async () => {
      try {
        // Request audio permission first
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
        setPermissionGranted(true);

        const devices = await navigator.mediaDevices.enumerateDevices();
        const mics = devices.filter((d) => d.kind === 'audioinput');
        // Put "default" device first, then the rest (excluding duplicate default)
        const sortDevices = (list) => {
          const def = list.find((d) => d.deviceId === 'default');
          const rest = list.filter((d) => d.deviceId !== 'default');
          return def ? [def, ...rest] : rest;
        };
        setMicDevices(sortDevices(mics));
        if (sinkIdSupported) {
          setSpeakerDevices(sortDevices(devices.filter((d) => d.kind === 'audiooutput')));
        }
      } catch (err) {
        console.warn('AudioDevicePopover: Could not enumerate devices', err);
      }
    };

    enumerate();
    navigator.mediaDevices.addEventListener('devicechange', enumerate);
    return () => navigator.mediaDevices.removeEventListener('devicechange', enumerate);
  }, [isOpen, sinkIdSupported]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    };
    const t = setTimeout(() => document.addEventListener('pointerdown', handleClick), 50);
    return () => {
      clearTimeout(t);
      document.removeEventListener('pointerdown', handleClick);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const deviceLabel = (d) =>
    d.deviceId === 'default' ? 'Default' : (d.label || `Device ${d.deviceId.slice(0, 8)}…`);

  const anchorPositions = {
    left: { right: 'calc(100% + 12px)', top: '0' }, // Align to top to avoid jarring repositioning
    right: { left: 'calc(100% + 12px)', top: '0' }, // Opens toward the phone from the left toolbar
    top: { bottom: 'calc(100% + 12px)', left: '50%', transform: 'translateX(-50%)' },
    bottom: { top: 'calc(100% + 12px)', right: 0 },
    none: {}, // For modal usage - no positioning needed
  };

  const popoverStyle = {
    position: anchor === 'none' ? 'relative' : 'absolute',
    zIndex: 10001,
    ...(anchorPositions[anchor] || anchorPositions.left),
    width: anchor === 'none' ? '100%' : '340px',
    maxWidth: anchor === 'none' ? '420px' : '340px',
    background: 'var(--cara-surface)',
    border: '2px solid rgba(68, 194, 253, 0.2)',
    borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.7), 0 0 40px rgba(68, 194, 253, 0.1)',
    overflow: 'hidden',
    fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif',
    color: 'var(--cara-text)',
  };

  const sectionTitle = {
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    color: 'var(--cara-text-muted)',
    marginBottom: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  };

  const selectStyle = {
    width: '100%',
    padding: '10px 32px 10px 12px',
    borderRadius: '10px',
    border: '1px solid rgba(68, 194, 253, 0.25)',
    background: 'var(--cara-input-bg)',
    color: 'var(--cara-text)',
    fontSize: '13px',
    fontFamily: 'inherit',
    cursor: 'pointer',
    outline: 'none',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2344C2FD' d='M2 4l4 4 4-4'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    backgroundSize: '12px',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  };

  // Whether this is modal mode (for mobile)
  const isModal = anchor === 'none';

  return (
    <div 
      ref={ref} 
      style={{
        ...popoverStyle,
        animation: isModal ? 'none' : 'slideIn 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      }}
    >
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateX(10px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          @keyframes slideInMobile {
            from {
              opacity: 0;
              transform: scale(0.92);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
          @keyframes shine {
            0%, 100% { transform: translateX(-100%); }
            50% { transform: translateX(100%); }
          }
        `}
      </style>

      {/* Header - only show in modal mode */}
      {isModal && (
      <div 
        style={{
          padding: '20px',
          borderBottom: '1px solid rgba(107, 114, 128, 0.3)',
          background: 'var(--cara-surface-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Subtle animated shine effect */}
        <div 
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.05), transparent)',
            animation: 'shine 3s ease-in-out infinite',
            transform: 'translateX(-100%)',
          }}
        />

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          position: 'relative',
          zIndex: 10
        }}>
          <span className="material-icons" style={{ fontSize: '20px', color: '#44C2FD' }}>tune</span>
          <div>
            <h3 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: 'var(--cara-text)',
              letterSpacing: '0.02em'
            }}>
              Settings
            </h3>
            <p style={{
              margin: 0,
              fontSize: '12px',
              color: '#9ca3af'
            }}>
              Devices & preferences
            </p>
          </div>
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#9ca3af',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            transition: 'all 0.2s',
            position: 'relative',
            zIndex: 10
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(68, 194, 253, 0.1)';
            e.currentTarget.style.color = 'var(--cara-text)';
            e.currentTarget.style.transform = 'rotate(90deg)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = '#9ca3af';
            e.currentTarget.style.transform = 'rotate(0deg)';
          }}
        >
          <span className="material-icons" style={{ fontSize: '24px' }}>close</span>
        </button>
      </div>
      )}

      {/* Content area. On mobile (modal) the section list can exceed the
          viewport — allow it to scroll. */}
      <div style={{ padding: isModal ? '16px' : '12px', maxHeight: isModal ? '70vh' : undefined, overflowY: isModal ? 'auto' : undefined }}>


      {!permissionGranted && (
        <div style={{ fontSize: '12px', color: 'var(--cara-text-muted)', textAlign: 'center', padding: '12px 0' }}>
          Microphone permission required to list audio devices.
        </div>
      )}

      {/* Microphone section */}
      {micDevices.length > 0 && (
        <div style={{ marginBottom: sinkIdSupported && speakerDevices.length > 0 ? '14px' : '14px' }}>
          <div style={sectionTitle}>
            <span className="material-icons" style={{ fontSize: '12px' }}>mic</span>
            Microphone
          </div>
          <select
            value={selectedMicId}
            onChange={(e) => dispatch(setSelectedMic(e.target.value))}
            style={selectStyle}
            onFocus={(e) => {
              e.target.style.borderColor = 'rgba(68, 194, 253, 0.5)';
              e.target.style.boxShadow = '0 0 0 2px rgba(68, 194, 253, 0.15)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(68, 194, 253, 0.25)';
              e.target.style.boxShadow = 'none';
            }}
          >
            {micDevices.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {deviceLabel(d)}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Speaker section - always show, with message if not supported */}
      <div style={{ marginBottom: '14px' }}>
        <div style={sectionTitle}>
          <span className="material-icons" style={{ fontSize: '12px' }}>volume_up</span>
          Speaker
        </div>
        {sinkIdSupported && speakerDevices.length > 0 ? (
          <select
            value={selectedSpeakerId}
            onChange={(e) => dispatch(setSelectedSpeaker(e.target.value))}
            style={selectStyle}
            onFocus={(e) => {
              e.target.style.borderColor = 'rgba(68, 194, 253, 0.5)';
              e.target.style.boxShadow = '0 0 0 2px rgba(68, 194, 253, 0.15)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(68, 194, 253, 0.25)';
              e.target.style.boxShadow = 'none';
            }}
          >
            {speakerDevices.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {deviceLabel(d)}
              </option>
            ))}
          </select>
        ) : !sinkIdSupported ? (
          <div
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '10px',
              border: '1px solid rgba(107, 114, 128, 0.3)',
              background: 'linear-gradient(135deg, rgba(44, 53, 68, 0.6) 0%, rgba(26, 35, 50, 0.6) 100%)',
              color: '#6b7280',
              fontSize: '12px',
              fontFamily: 'inherit',
              lineHeight: '1.4',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span className="material-icons" style={{ fontSize: '14px' }}>info</span>
              Speaker selection is not available on this browser. Audio will play through your device's default speaker.
            </span>
          </div>
        ) : (
          <div
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '10px',
              border: '1px solid rgba(68, 194, 253, 0.25)',
              background: 'var(--cara-input-bg)',
              color: 'var(--cara-text-muted)',
              fontSize: '13px',
              fontFamily: 'inherit',
            }}
          >
            Default Speaker
          </div>
        )}
      </div>

      {/* Default Agent Language — mobile only; desktop uses the toolbar menu. */}
      {isModal && (
      <>
      <div style={{ marginBottom: '14px' }}>
        <div style={sectionTitle}>
          <span className="material-icons" style={{ fontSize: '12px' }}>translate</span>
          Default Agent Language
        </div>
        <select
          value={selectedLocale}
          onChange={(e) => dispatch(setSelectedLocale(e.target.value))}
          style={selectStyle}
          onFocus={(e) => {
            e.target.style.borderColor = 'rgba(68, 194, 253, 0.5)';
            e.target.style.boxShadow = '0 0 0 2px rgba(68, 194, 253, 0.15)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'rgba(68, 194, 253, 0.25)';
            e.target.style.boxShadow = 'none';
          }}
        >
          {/* Preserve a current value that isn't in the preset list. */}
          {!CALL_LANGUAGES.some((l) => l.code === selectedLocale) && (
            <option value={selectedLocale}>{selectedLocale}</option>
          )}
          {CALL_LANGUAGES.map((l) => (
            <option key={l.code} value={l.code}>{l.label}</option>
          ))}
        </select>
      </div>

      {/* Divider */}
      <div style={{
        margin: '16px 0',
        borderTop: '1px solid rgba(255,255,255,0.08)'
      }} />
      </>
      )}

      {/* Device Frame section - mobile only; desktop uses the toolbar toggle. */}
      {isModal && (
      <>
      <div style={{ marginBottom: '14px' }}>
        <div style={sectionTitle}>
          <span className="material-icons" style={{ fontSize: '12px' }}>devices</span>
          Device Frame
        </div>
        <p style={{
          margin: '0 0 10px 0',
          fontSize: '11px',
          color: '#6b7280',
          lineHeight: '1.4',
          paddingLeft: '4px'
        }}>
          Change the simulated device display frame
        </p>
        <div className="device-buttons-container" style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => {
              if (deviceType !== 'phone') {
                dispatch(toggleDevice());
              }
            }}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              padding: '8px 6px',
              borderRadius: '8px',
              border: deviceType === 'phone' 
                ? '2px solid rgba(68, 194, 253, 0.5)' 
                : '1px solid rgba(68, 194, 253, 0.25)',
              background: deviceType === 'phone'
                ? 'linear-gradient(135deg, rgba(68, 194, 253, 0.2) 0%, rgba(35, 166, 213, 0.15) 100%)'
                : 'var(--cara-input-bg)',
              color: deviceType === 'phone' ? '#44C2FD' : 'var(--cara-text-muted)',
              fontSize: '11px',
              fontFamily: 'inherit',
              cursor: 'pointer',
              outline: 'none',
              transition: 'all 0.2s',
            }}
          >
            <span className="material-icons" style={{ fontSize: '20px' }}>smartphone</span>
            <span style={{ fontWeight: '600' }}>Phone</span>
          </button>
          <button
            onClick={() => {
              if (deviceType !== 'tablet') {
                dispatch(toggleDevice());
              }
            }}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              padding: '8px 6px',
              borderRadius: '8px',
              border: deviceType === 'tablet' 
                ? '2px solid rgba(68, 194, 253, 0.5)' 
                : '1px solid rgba(68, 194, 253, 0.25)',
              background: deviceType === 'tablet'
                ? 'linear-gradient(135deg, rgba(68, 194, 253, 0.2) 0%, rgba(35, 166, 213, 0.15) 100%)'
                : 'var(--cara-input-bg)',
              color: deviceType === 'tablet' ? '#44C2FD' : 'var(--cara-text-muted)',
              fontSize: '11px',
              fontFamily: 'inherit',
              cursor: 'pointer',
              outline: 'none',
              transition: 'all 0.2s',
            }}
          >
            <span className="material-icons" style={{ fontSize: '20px' }}>tablet</span>
            <span style={{ fontWeight: '600' }}>Tablet</span>
          </button>
        </div>
      </div>

      {/* Divider */}
      <div style={{ 
        margin: '16px 0', 
        borderTop: '1px solid rgba(255,255,255,0.08)' 
      }} />
      </>
      )}

      {/* Appearance — Light / Dark tiles. Mobile only; desktop uses the toolbar toggle. */}
      {isModal && (
      <div style={{ marginBottom: '14px' }}>
        <div style={sectionTitle}>
          <span className="material-icons" style={{ fontSize: '12px' }}>contrast</span>
          Appearance
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => dispatch(setTheme('light'))}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              padding: '8px 6px',
              borderRadius: '8px',
              border: isLightMode ? '2px solid rgba(68, 194, 253, 0.5)' : '1px solid rgba(68, 194, 253, 0.25)',
              background: isLightMode
                ? 'linear-gradient(135deg, rgba(68, 194, 253, 0.2) 0%, rgba(35, 166, 213, 0.15) 100%)'
                : 'var(--cara-input-bg)',
              color: isLightMode ? '#44C2FD' : 'var(--cara-text-muted)',
              fontSize: '11px',
              fontFamily: 'inherit',
              cursor: 'pointer',
              outline: 'none',
              transition: 'all 0.2s',
            }}
          >
            <span className="material-icons" style={{ fontSize: '20px' }}>light_mode</span>
            <span style={{ fontWeight: '600' }}>Light</span>
          </button>
          <button
            onClick={() => dispatch(setTheme('dark'))}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              padding: '8px 6px',
              borderRadius: '8px',
              border: !isLightMode ? '2px solid rgba(68, 194, 253, 0.5)' : '1px solid rgba(68, 194, 253, 0.25)',
              background: !isLightMode
                ? 'linear-gradient(135deg, rgba(68, 194, 253, 0.2) 0%, rgba(35, 166, 213, 0.15) 100%)'
                : 'var(--cara-input-bg)',
              color: !isLightMode ? '#44C2FD' : 'var(--cara-text-muted)',
              fontSize: '11px',
              fontFamily: 'inherit',
              cursor: 'pointer',
              outline: 'none',
              transition: 'all 0.2s',
            }}
          >
            <span className="material-icons" style={{ fontSize: '20px' }}>dark_mode</span>
            <span style={{ fontWeight: '600' }}>Dark</span>
          </button>
        </div>
      </div>
      )}

      {/* Alerts toggle */}
      <div style={{ marginBottom: '14px' }}>
        <button
          onClick={() => dispatch(toggleToasts())}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 12px',
            borderRadius: '10px',
            border: '1px solid rgba(68, 194, 253, 0.25)',
            background: toastsEnabled 
              ? 'linear-gradient(135deg, rgba(68, 194, 253, 0.2) 0%, rgba(35, 166, 213, 0.15) 100%)'
              : 'var(--cara-input-bg)',
            color: 'var(--cara-text)',
            fontSize: '13px',
            fontFamily: 'inherit',
            cursor: 'pointer',
            outline: 'none',
            transition: 'all 0.2s',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="material-icons" style={{ fontSize: '16px', color: toastsEnabled ? '#44C2FD' : 'var(--cara-text-muted)' }}>
              {toastsEnabled ? 'notifications_active' : 'notifications_off'}
            </span>
            <span>Alerts</span>
          </span>
          <span style={{
            width: '36px',
            height: '20px',
            borderRadius: '10px',
            background: toastsEnabled ? '#44C2FD' : '#3a4556',
            position: 'relative',
            transition: 'background 0.2s',
          }}>
            <span style={{
              position: 'absolute',
              top: '2px',
              left: toastsEnabled ? '18px' : '2px',
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              background: '#fff',
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }} />
          </span>
        </button>
        <p style={{
          margin: '6px 0 0 0',
          fontSize: '11px',
          color: '#6b7280',
          lineHeight: '1.4',
          paddingLeft: '4px'
        }}>
          Display notifications as background actions are performed
        </p>
      </div>

      {/* Autopilot toggle */}
      <div style={{ marginBottom: '14px' }}>
        <button
          onClick={() => dispatch(toggleFollowMode())}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 12px',
            borderRadius: '10px',
            border: '1px solid rgba(68, 194, 253, 0.25)',
            background: followMode 
              ? 'linear-gradient(135deg, rgba(68, 194, 253, 0.2) 0%, rgba(35, 166, 213, 0.15) 100%)'
              : 'var(--cara-input-bg)',
            color: 'var(--cara-text)',
            fontSize: '13px',
            fontFamily: 'inherit',
            cursor: 'pointer',
            outline: 'none',
            transition: 'all 0.2s',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="material-icons" style={{ fontSize: '16px', color: followMode ? '#44C2FD' : 'var(--cara-text-muted)' }}>
              smart_toy
            </span>
            <span>Autopilot</span>
          </span>
          <span style={{
            width: '36px',
            height: '20px',
            borderRadius: '10px',
            background: followMode ? '#44C2FD' : '#3a4556',
            position: 'relative',
            transition: 'background 0.2s',
          }}>
            <span style={{
              position: 'absolute',
              top: '2px',
              left: followMode ? '18px' : '2px',
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              background: '#fff',
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }} />
          </span>
        </button>
        <p style={{
          margin: '6px 0 0 0',
          fontSize: '11px',
          color: '#6b7280',
          lineHeight: '1.4',
          paddingLeft: '4px'
        }}>
          Automatically open emails, SMS, etc. as they are received
        </p>
      </div>

      {/* iOS Header toggle - only shown on mobile/tablet */}
      {isModal && (
      <div style={{ marginBottom: '14px' }}>
        <button
          onClick={() => dispatch(toggleMobileStatusBar())}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 12px',
            borderRadius: '10px',
            border: '1px solid rgba(68, 194, 253, 0.25)',
            background: showMobileStatusBar 
              ? 'linear-gradient(135deg, rgba(68, 194, 253, 0.2) 0%, rgba(35, 166, 213, 0.15) 100%)'
              : 'var(--cara-input-bg)',
            color: 'var(--cara-text)',
            fontSize: '13px',
            fontFamily: 'inherit',
            cursor: 'pointer',
            outline: 'none',
            transition: 'all 0.2s',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="material-icons" style={{ fontSize: '16px', color: showMobileStatusBar ? '#44C2FD' : 'var(--cara-text-muted)' }}>
              signal_cellular_alt
            </span>
            <span>iOS Header</span>
          </span>
          <span style={{
            width: '36px',
            height: '20px',
            borderRadius: '10px',
            background: showMobileStatusBar ? '#44C2FD' : '#3a4556',
            position: 'relative',
            transition: 'background 0.2s',
          }}>
            <span style={{
              position: 'absolute',
              top: '2px',
              left: showMobileStatusBar ? '18px' : '2px',
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              background: '#fff',
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }} />
          </span>
        </button>
        <p style={{
          margin: '6px 0 0 0',
          fontSize: '11px',
          color: '#6b7280',
          lineHeight: '1.4',
          paddingLeft: '4px'
        }}>
          Show the simulated iOS status bar with time, signal, and battery
        </p>
      </div>
      )}

      {/* Employee Events toggle */}
      <div style={{ marginBottom: '14px' }}>
        <button
          onClick={() => dispatch(toggleEmployeeEvents())}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 12px',
            borderRadius: '10px',
            border: '1px solid rgba(68, 194, 253, 0.25)',
            background: employeeEventsEnabled
              ? 'linear-gradient(135deg, rgba(68, 194, 253, 0.2) 0%, rgba(35, 166, 213, 0.15) 100%)'
              : 'var(--cara-input-bg)',
            color: 'var(--cara-text)',
            fontSize: '13px',
            fontFamily: 'inherit',
            cursor: 'pointer',
            outline: 'none',
            transition: 'all 0.2s',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="material-icons" style={{ fontSize: '16px', color: employeeEventsEnabled ? '#44C2FD' : 'var(--cara-text-muted)' }}>
              support_agent
            </span>
            <span>Employee Events</span>
          </span>
          <span style={{
            width: '36px',
            height: '20px',
            borderRadius: '10px',
            background: employeeEventsEnabled ? '#44C2FD' : '#3a4556',
            position: 'relative',
            transition: 'background 0.2s',
          }}>
            <span style={{
              position: 'absolute',
              top: '2px',
              left: employeeEventsEnabled ? '18px' : '2px',
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              background: '#fff',
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }} />
          </span>
        </button>
        <p style={{
          margin: '6px 0 0 0',
          fontSize: '11px',
          color: '#6b7280',
          lineHeight: '1.4',
          paddingLeft: '4px'
        }}>
          Auto-open the employee side panel when agent events arrive
        </p>
      </div>

      {/* iPad desktop-in-landscape toggle — only visible on iPad */}
      {isIPad && (
      <div style={{ marginBottom: '14px' }}>
        <button
          onClick={() => dispatch(toggleIPadDesktopLandscape())}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 12px',
            borderRadius: '10px',
            border: '1px solid rgba(68, 194, 253, 0.25)',
            background: iPadDesktopLandscape
              ? 'linear-gradient(135deg, rgba(68, 194, 253, 0.2) 0%, rgba(35, 166, 213, 0.15) 100%)'
              : 'var(--cara-input-bg)',
            color: 'var(--cara-text)',
            fontSize: '13px',
            fontFamily: 'inherit',
            cursor: 'pointer',
            outline: 'none',
            transition: 'all 0.2s',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="material-icons" style={{ fontSize: '16px', color: iPadDesktopLandscape ? '#44C2FD' : 'var(--cara-text-muted)' }}>
              desktop_windows
            </span>
            <span>Desktop in Landscape</span>
          </span>
          <span style={{
            width: '36px',
            height: '20px',
            borderRadius: '10px',
            background: iPadDesktopLandscape ? '#44C2FD' : '#3a4556',
            position: 'relative',
            transition: 'background 0.2s',
          }}>
            <span style={{
              position: 'absolute',
              top: '2px',
              left: iPadDesktopLandscape ? '18px' : '2px',
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              background: '#fff',
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }} />
          </span>
        </button>
        <p style={{
          margin: '6px 0 0 0',
          fontSize: '11px',
          color: '#6b7280',
          lineHeight: '1.4',
          paddingLeft: '4px'
        }}>
          Use the desktop layout (side panel etc.) when iPad is in landscape
        </p>
      </div>
      )}

      {/* Style for select option elements (dark theme) */}
      <style>{`
        div[style*="AudioDevicePopover"] select option,
        select option {
          background: #1e2433;
          color: #e0e4ea;
          padding: 8px;
        }
      `}</style>
      </div>
    </div>
  );
}
